/**
 * Generato automaticamente.
 * framework-cmp-procedimenti
 */
import { useMutation, useQuery } from 'react-query';
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { apiClientRequest } from '../../../client/apiClient';
import type { ErrorType } from '../../../client/apiClient';
import type {
  GetBackPdndC020VerificaAuditParams,
  PostBackPdndC020Verifica200,
  VerificaC020Request,
  WsProblema,
  WsRispostaPaginataListPdndVerificaAuditDto,
} from '.././schemas';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

// eslint-disable-next-line
type SecondParameter<T extends (...args: any) => any> = T extends (
  config: any,
  args: infer P,
) => any
  ? P
  : never;

/**
 * Verifica la residenza e ne traccia la richiesta
 * @summary Chiama per accertamento della residenza integrata con PDND C020
 */
export const postBackPdndC020Verifica = (
  verificaC020Request: VerificaC020Request,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<PostBackPdndC020Verifica200>(
    {
      url: `/framework-bo-procedimenti/pdnd/c020/verifica`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: verificaC020Request,
    },
    options,
  );
};

export const getPostBackPdndC020VerificaMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postBackPdndC020Verifica>>,
    TError,
    { data: VerificaC020Request },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postBackPdndC020Verifica>>,
  TError,
  { data: VerificaC020Request },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postBackPdndC020Verifica>>,
    { data: VerificaC020Request }
  > = props => {
    const { data } = props ?? {};

    return postBackPdndC020Verifica(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostBackPdndC020VerificaMutationResult = NonNullable<
  Awaited<ReturnType<typeof postBackPdndC020Verifica>>
>;
export type PostBackPdndC020VerificaMutationBody = VerificaC020Request;
export type PostBackPdndC020VerificaMutationError = ErrorType<unknown>;

/**
 * @summary Chiama per accertamento della residenza integrata con PDND C020
 */
export const usePostBackPdndC020Verifica = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postBackPdndC020Verifica>>,
    TError,
    { data: VerificaC020Request },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPostBackPdndC020VerificaMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Ottiene l'elenco delle richieste di verifica inoltrate a PDND.
 * @summary Elenco richieste di verifica inoltrate a PDND
 */
export const getBackPdndC020VerificaAudit = (
  params?: GetBackPdndC020VerificaAuditParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<WsRispostaPaginataListPdndVerificaAuditDto>(
    {
      url: `/framework-bo-procedimenti/pdnd/c020/verifica/audit`,
      method: 'get',
      params,
      signal,
    },
    options,
  );
};

export const getGetBackPdndC020VerificaAuditQueryKey = (
  params?: GetBackPdndC020VerificaAuditParams,
) =>
  [
    `/framework-bo-procedimenti/pdnd/c020/verifica/audit`,
    ...(params ? [params] : []),
  ] as const;

export const getGetBackPdndC020VerificaAuditQueryOptions = <
  TData = Awaited<ReturnType<typeof getBackPdndC020VerificaAudit>>,
  TError = ErrorType<WsProblema>,
>(
  params?: GetBackPdndC020VerificaAuditParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBackPdndC020VerificaAudit>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getBackPdndC020VerificaAudit>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetBackPdndC020VerificaAuditQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getBackPdndC020VerificaAudit>>
  > = ({ signal }) =>
    getBackPdndC020VerificaAudit(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetBackPdndC020VerificaAuditQueryResult = NonNullable<
  Awaited<ReturnType<typeof getBackPdndC020VerificaAudit>>
>;
export type GetBackPdndC020VerificaAuditQueryError = ErrorType<WsProblema>;

/**
 * @summary Elenco richieste di verifica inoltrate a PDND
 */
export const useGetBackPdndC020VerificaAudit = <
  TData = Awaited<ReturnType<typeof getBackPdndC020VerificaAudit>>,
  TError = ErrorType<WsProblema>,
>(
  params?: GetBackPdndC020VerificaAuditParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBackPdndC020VerificaAudit>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetBackPdndC020VerificaAuditQueryOptions(
    params,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};
